import React from "react";
import { exportCSV } from "./exportCSV";

/**
 * The member register.
 *
 * Twelve columns do not fit a phone, and a table scrolled sideways with one
 * thumb is not a register anyone can read. So the table is desktop-only and a
 * caller may pass `mobileCard` — a per-row renderer shown below `lg` in its
 * place. Toolbar, export and pagination are shared, so the phone gets the same
 * register rather than a cut-down one.
 *
 * `rowClass(row)` optionally colours a row by what is in it — a deceased
 * member's, say — on both the table row and its card.
 *
 * Without `mobileCard` or `rowClass` this behaves exactly as it always did.
 */
export default function DataTable({
  columns,
  data,
  pagination,
  onPageChange,
  sort,
  setSort,
  dir,
  setDir,
  selected,
  setSelected,
  canExport = false,
  mobileCard,
  rowClass,
  // What this register is called. Defaulted so the member list, which named it
  // in the markup, is untouched — the firm register reuses the same table.
  title = "Members List",
}: any) {
  const toggleSort = (key: string) => {
    if (sort === key) {
      setDir(dir === "asc" ? "desc" : "asc");
    } else {
      setSort(key);
      setDir("asc");
    }
  };

  // Sorting is the table header's job, and the table is gone on a phone — so
  // the sort is offered as a select there instead of being lost with it.
  const sortableColumns = (columns as any[]).filter((c) => c.sortable !== false);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      {/* Upper Toolbar */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4 border-b border-gray-100">
        <div className="text-sm font-bold text-gray-700">
          {title} ({pagination?.total?.toLocaleString()})
        </div>
        {canExport && (
          <button
            onClick={() => exportCSV(data)}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-all"
          >
            Export CSV
          </button>
        )}
      </div>

      {/* Mobile: sort control + the rows as cards. */}
      {mobileCard && (
        <div className="lg:hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest shrink-0">
              Sort
            </label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-bold bg-white"
            >
              {sortableColumns.map((c: any) => (
                <option key={c.key} value={c.key}>{c.header}</option>
              ))}
            </select>
            <button
              onClick={() => setDir(dir === "asc" ? "desc" : "asc")}
              className="shrink-0 border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-bold bg-white text-gray-600"
            >
              {dir === "asc" ? "▲ Asc" : "▼ Desc"}
            </button>
          </div>

          <div className="divide-y divide-gray-100">
            {data.length === 0 && (
              <p className="px-4 py-10 text-center text-sm font-medium text-gray-400">
                No members match these filters.
              </p>
            )}
            {data.map((row: any) => (
              <div key={row.id} className={`p-4 ${rowClass?.(row) ?? ""}`}>
                {mobileCard(row)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table Container with Fixed Height for Sticky Header */}
      <div className={`overflow-auto max-h-[650px] relative ${mobileCard ? "hidden lg:block" : ""}`}>
        <table className="w-full text-left border-separate border-spacing-0">
          <thead className="relative z-20">
            <tr>
              {/* Sticky Checkbox Header */}
              <th className="sticky top-0 z-30 bg-gray-100 p-4 border-b border-gray-200">
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  onChange={(e) =>
                    setSelected(
                      e.target.checked ? data.map((d: any) => d.id) : []
                    )
                  }
                  checked={data.length > 0 && selected.length === data.length}
                />
              </th>

              {/* Sticky Data Headers */}
              {columns.map((c: any) => (
                <th
                  key={c.key}
                  className="sticky top-0 z-30 bg-gray-100 px-4 py-4 text-xs font-bold text-gray-600 uppercase tracking-tight border-b border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => toggleSort(c.key)}
                >
                  <div className="flex items-center gap-1">
                    {c.header}
                    {sort === c.key && (
                      <span className="text-blue-600">
                        {dir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white">
            {data.map((row: any) => (
              <tr
                key={row.id}
                // A caller may colour a row by what is in it. When it does, its
                // classes REPLACE the striping rather than joining it — an
                // `even:` background and a plain one carry the same weight, so
                // keeping both would tint every other marked row back to grey.
                className={`group transition-colors ${
                  rowClass?.(row) || "even:bg-gray-50/80 hover:bg-blue-50"
                }`}
              >
                <td className="p-4 border-b border-gray-100">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600"
                    checked={selected.includes(row.id)}
                    onChange={() => {
                      setSelected(
                        selected.includes(row.id)
                          ? selected.filter((x: any) => x !== row.id)
                          : [...selected, row.id]
                      );
                    }}
                  />
                </td>
                {columns.map((c: any) => (
                  <td
                    key={c.key}
                    className="px-4 py-4 text-sm text-gray-700 border-b border-gray-100"
                  >
                    {c.accessor ? c.accessor(row) : row[c.key] || "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="p-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex flex-col sm:flex-row gap-3 justify-between items-center">
        <span className="text-xs text-gray-500 font-medium">
          Showing {pagination?.from} to {pagination?.to} of {pagination?.total}
        </span>

        <div className="flex flex-wrap justify-center gap-1">
          {(pagination?.links ?? []).map((link: any, i: number) => {
            const isPrev = link.label.includes("Prev");
            const isNext = link.label.includes("Next");
            return (
              <button
                key={i}
                disabled={!link.url}
                onClick={() =>
                  onPageChange(
                    isNext
                      ? pagination.current_page + 1
                      : isPrev
                      ? pagination.current_page - 1
                      : parseInt(link.label)
                  )
                }
                className={`px-3 py-1.5 text-xs font-bold border rounded-md transition-all ${
                  link.active
                    ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                    : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                } ${!link.url && "opacity-40 cursor-not-allowed"} ${
                  // A register runs to dozens of pages; the numbered links are
                  // dropped on a phone, where prev/next is how it is paged.
                  isPrev || isNext ? "" : "hidden sm:inline-block"
                }`}
              >
                {isPrev ? "←" : isNext ? "→" : link.label}
              </button>
            );
          })}
          {pagination?.current_page != null && (
            <span className="sm:hidden px-3 py-1.5 text-xs font-bold text-gray-500">
              Page {pagination.current_page} of {pagination.last_page}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
